import { type ReactElement, useEffect, useMemo, useState } from 'react';
import type { AiCredentialsView, GovernedSettingSummaryView } from '@vello/api-client';
import { formatMoney } from '@vello/format';
import { Badge, Button, Cluster, HairlineRow, HairlineRowMain, Notice, Panel, SectionTitle, Spacer, Stack, Text } from '@vello/web-theme';
import {
  AI_CREDENTIALS_ROUTE,
  CENTRAL_AI_SETTING_KEYS,
  centralAiOverview,
  countriesOfMarkets,
  summarizeCentralAiValue,
  type CentralAiSummary,
  type OverviewCell,
  type OverviewRow,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useScopeName } from '../i18n/scope-name';
import { GovernedSettingsScreen } from './GovernedSettingsScreen';

/**
 * **Configurações › Atendimento e IA** (D631, Digital Central phase 0, doc 25 section 11) - the settings of the Central's automatic assistant
 * and of every AI the platform runs, grouped by where they apply:
 *  - the assistant's identity (name, languages, the disclosure line that says it is automatic), the money it may refund alone, the payment
 *    options it may offer, the AI model for each purpose, the speech engine and the monthly AI budget;
 *  - an overview by country (the platform's default first), then the same four-eyes flow as "Regras governadas" for the key and the place chosen
 *    (typed form, preview, a reason, a DIFFERENT operator approves, versions and timeline);
 *  - the state of the provider credentials, which live in the vault (Integrações), never in a setting.
 * Nothing is written without a second pair of eyes; where nothing is set, the safe reading holds (no automatic money, no payment option, no AI call).
 */
export function CentralAiSettingsScreen(): ReactElement {
  const { markets } = useAdmin();
  const countries = useMemo(() => countriesOfMarkets(markets), [markets]);
  return (
    <GovernedSettingsScreen
      only={CENTRAL_AI_SETTING_KEYS}
      heading={{ title: 'centralai.title', subtitle: 'centralai.subtitle', explain: 'centralai.explain' }}
      countries={countries}
      hidePolicies
      showKeys={false}
      renderAbove={({ summaries, open, selectedKey }) => (
        <>
          <Overview summaries={summaries} countries={countries} open={open} selectedKey={selectedKey} />
          <Credentials summaries={summaries} />
        </>
      )}
    />
  );
}

// ---- the overview --------------------------------------------------------------------------------

function Overview({ summaries, countries, open, selectedKey }: {
  summaries: readonly GovernedSettingSummaryView[];
  countries: readonly string[];
  open: (key: string, scope: 'platform' | 'country', scopeRef: string) => void;
  selectedKey: string | undefined;
}): ReactElement {
  const { t } = useT();
  const rows = useMemo(() => centralAiOverview(summaries, countries), [summaries, countries]);
  return (
    <Stack gap={4}>
      <SectionTitle>{t('centralai.overview.title')}</SectionTitle>
      <Text as="p" role="caption" tone="secondary">{t('centralai.overview.hint')}</Text>
      {rows.map((row) => <PlaceCard key={`${row.scope}|${row.scopeRef}`} row={row} open={open} selectedKey={selectedKey} />)}
    </Stack>
  );
}

function PlaceCard({ row, open, selectedKey }: { row: OverviewRow; open: (key: string, scope: 'platform' | 'country', scopeRef: string) => void; selectedKey: string | undefined }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const scopeName = useScopeName();
  const name = scopeName({ scopeType: row.scope === 'platform' ? 'GLOBAL' : 'COUNTRY', ...(row.scopeRef !== '' ? { scopeId: row.scopeRef } : {}) });
  const cells = row.cells.filter((c) => c.state !== 'NOT_ALLOWED');
  return (
    <Panel>
      <Stack gap={3}>
        <Cluster gap={3} align="baseline" wrap>
          <Text as="h3" role="title">{name}</Text>
          {row.scope === 'platform' && <Text role="caption" tone="tertiary">{t('centralai.overview.platform.hint')}</Text>}
        </Cluster>
        {cells.map((cell) => (
          <HairlineRow key={cell.key} align="baseline">
            <Text role="label">{v('settings.key', cell.key)}</Text>
            <StateBadge cell={cell} />
            <HairlineRowMain><CellSummary cell={cell} /></HairlineRowMain>
            <Button
              size="sm"
              variant="secondary"
              aria-pressed={selectedKey === cell.key}
              aria-label={t('centralai.overview.open', { action: t(cell.state === 'SET' ? 'centralai.overview.edit' : 'centralai.overview.set'), setting: v('settings.key', cell.key), place: name })}
              onClick={() => open(cell.key, row.scope, row.scopeRef)}
            >
              {t(cell.state === 'SET' ? 'centralai.overview.edit' : 'centralai.overview.set')}
            </Button>
          </HairlineRow>
        ))}
      </Stack>
    </Panel>
  );
}

const STATE_TONE: Readonly<Record<OverviewCell['state'], 'neutral' | 'success' | 'warning' | 'danger'>> = {
  NOT_ALLOWED: 'neutral', UNSET: 'warning', DEFAULT: 'neutral', SET: 'success', INHERITED: 'neutral', FAIL_CLOSED: 'danger',
};

function StateBadge({ cell }: { cell: OverviewCell }): ReactElement {
  const { t } = useT();
  return (
    <Cluster gap={2} align="baseline">
      <Badge tone={STATE_TONE[cell.state]}>{t(`centralai.state.${cell.state}`, { n: cell.version ?? 0 })}</Badge>
      {cell.pending > 0 && <Badge tone="warning">{t('centralai.state.pending', { n: cell.pending })}</Badge>}
    </Cluster>
  );
}

/** What a cell says in plain words: the value in force, or what happens while nothing is set. */
function CellSummary({ cell }: { cell: OverviewCell }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  if (cell.state === 'SET' || cell.state === 'INHERITED') {
    const summary = summarizeCentralAiValue(cell.key, cell.value);
    return summary === undefined ? <Text role="caption" tone="secondary">{t('centralai.summary.unreadable')}</Text> : <SummaryText summary={summary} />;
  }
  if (cell.state === 'FAIL_CLOSED') return <Text role="caption" tone="secondary">{t('centralai.summary.failClosed')}</Text>;
  if (cell.state === 'NOT_ALLOWED') return <></>;
  // Nothing set: the safe reading of each key.
  return <Text role="caption" tone="secondary">{v('central_ai.unset_reading', cell.key)}</Text>;
}

function SummaryText({ summary }: { summary: CentralAiSummary }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const money = (amountMinor: bigint, currency: string): string => formatMoney({ amountMinor, currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
  switch (summary.kind) {
    case 'assistant':
      return <Text role="caption" tone="secondary">{t('centralai.summary.assistant', { name: summary.displayName, languages: summary.languages.map((l) => v('central.assistant_language', l)).join(', ') })}</Text>;
    case 'refundCaps':
      return <Text role="caption" tone="secondary">{t('centralai.summary.caps', { order: money(summary.perOrderMinor, summary.currency), month: money(summary.perCustomerMonthMinor, summary.currency) })}</Text>;
    case 'orderPayments':
      return (
        <Stack gap={1}>
          {summary.services.map((s) => (
            <Text key={s.service} role="caption" tone="secondary">
              {t('centralai.summary.pay', { service: s.service.startsWith('ride:') ? t('centralai.pay.ride', { category: v('ride.category', s.service.slice(5)) }) : v('central.order_service', s.service), methods: s.methods.length === 0 ? t('centralai.pay.none') : s.methods.map((m) => v('central.payment_method_token', m)).join(', ') })}
            </Text>
          ))}
        </Stack>
      );
    case 'aiModels':
      return (
        <Stack gap={1}>
          {summary.purposes.map((p) => (
            <Text key={p.purpose} role="caption" tone="secondary">{t('centralai.summary.model', { purpose: v('ai.purpose', p.purpose), model: p.model })}</Text>
          ))}
        </Stack>
      );
    case 'speechEngine':
      return <Text role="caption" tone="secondary">{t(summary.engine === 'self_hosted' ? 'centralai.summary.speech.self' : 'centralai.summary.speech.openrouter', { name: summary.credentialRef ?? '' })}</Text>;
    case 'autonomy':
      return (
        <Stack gap={1}>
          <Text role="caption" tone="secondary">{t(summary.enabled ? 'centralai.summary.autonomy.on' : 'centralai.summary.autonomy.off', { level: v('central.autonomy_level', summary.defaultLevel) })}</Text>
          {summary.intents.map((i) => (
            <Text key={i.intent} role="caption" tone="secondary">{t('centralai.summary.autonomy.intent', { intent: v('velo.intent', i.intent), level: v('central.autonomy_level', i.level) })}</Text>
          ))}
          {summary.disabledChannels.length + summary.disabledIntents.length > 0 && (
            <Text role="caption" tone="secondary">
              {t('centralai.summary.autonomy.killed', { items: [...summary.disabledChannels.map((c) => v('conversation.channel', c)), ...summary.disabledIntents.map((i) => v('velo.intent', i))].join(', ') })}
            </Text>
          )}
        </Stack>
      );
    case 'aiBudget':
      return <Text role="caption" tone="secondary">{t('centralai.summary.budget', { amount: money(summary.amountMinor, summary.currency), alerts: summary.alertPercents.map((p) => `${String(p)}%`).join(', ') })}</Text>;
  }
}

// ---- the provider credentials --------------------------------------------------------------------

/** Where the OpenRouter key and the self-hosted speech endpoints stand. The secrets live in the vault (Integrações): this only reads their state and links there. */
function Credentials({ summaries }: { summaries: readonly GovernedSettingSummaryView[] }): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const [view, setView] = useState<AiCredentialsView | undefined>(undefined);
  const [unreadable, setUnreadable] = useState(false);
  // The endpoints worth listing are the ones a country's speech-engine setting names.
  const refs = useMemo(() => {
    const named = summaries.find((s) => s.key === 'ai.speechEngine')?.buckets.map((b) => (b.current?.value as { credentialRef?: unknown } | undefined)?.credentialRef) ?? [];
    return [...new Set(named.filter((r): r is string => typeof r === 'string' && r !== ''))].sort();
  }, [summaries]);
  const refsKey = refs.join(',');
  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await runtime.integrations.getAiCredentials(refsKey === '' ? [] : refsKey.split(','));
      if (!alive) return;
      setUnreadable(!res.ok);
      if (res.ok) setView(res.value);
    })();
    return () => {
      alive = false;
    };
  }, [runtime, refsKey]);
  return (
    <Panel>
      <Stack gap={3}>
        <SectionTitle>{t('centralai.credentials.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('centralai.credentials.hint')}</Text>
        {unreadable && <Notice tone="warning">{t('centralai.credentials.unreadable')}</Notice>}
        {view !== undefined && (
          <>
            <HairlineRow align="baseline">
              <Text role="label">{t('aiint.openrouter')}</Text>
              <Badge tone={view.openrouter.configured && view.openrouter.enabled ? 'success' : 'warning'}>
                {t(view.openrouter.configured ? (view.openrouter.enabled ? 'centralai.credentials.ready' : 'centralai.credentials.off') : 'centralai.credentials.missing')}
              </Badge>
            </HairlineRow>
            {view.speech.map((s) => (
              <HairlineRow key={s.ref} align="baseline">
                <Text role="label">{t('aiint.speech.named', { name: s.ref })}</Text>
                <Badge tone={s.configured && s.enabled ? 'success' : 'warning'}>
                  {t(s.configured ? (s.enabled ? 'centralai.credentials.ready' : 'centralai.credentials.off') : 'centralai.credentials.missing')}
                </Badge>
              </HairlineRow>
            ))}
          </>
        )}
        <Cluster gap={3}>
          <Spacer />
          <Button variant="secondary" onClick={() => navigate(AI_CREDENTIALS_ROUTE)}>{t('centralai.credentials.open')}</Button>
        </Cluster>
      </Stack>
    </Panel>
  );
}
